import { UUID } from '@hfc/common/src/types/UUID';
import { v4 as uuid } from 'uuid';
import { Address, dateHelpers, formatHelpers, getStore, Ploc, ServicesApiResponse, SfApiResponse, ToastTypes } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { GENDERS } from '@hfc/common/src/types/Genders';
import { PlanFilter, SmallBusinessApiFilters } from '@/application/smallBusiness/domain/SmallBusinessPlanFilters';
import { AddressValidationUseCase } from '@/application/shared/application/AddressValidationUseCase';
import { SaveAndExitUseCase } from '@/application/shared/application/SaveAndExitUseCase';
import { EnrollmentApi } from '@/application/shared/infrastructure/dataSource/EnrollmentApi';
import { Relationship, Relationships } from '@/application/smallBusiness/domain/SmallBusinessRelationships';
import { AddressValidationResponse } from '@/application/shared/domain/AddressValidationResponse';
import { SmallBusinessApplication } from '@/application/smallBusiness/domain/SmallBusinessApplication';
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import { SmallBusinessCompany } from '@/application/smallBusiness/domain/SmallBusinessCompany';
import { ContributionType, SmallBusinessContribution } from '@/application/smallBusiness/domain/SmallBusinessContribution';
import { SmallBusinessEmployee } from '@/application/smallBusiness/domain/SmallBusinessEmployee';
import { SmallBusinessApi } from '@/application/smallBusiness/infrastructure/dataSource/SmallBusinessApi';
import { FetchAvailablePlansUseCase } from '@/application/smallBusiness/application/FetchAvailablePlansUseCase';
import { SubmitApplicationUseCase } from '@/application/smallBusiness/application/SubmitApplicationUseCase';
import { GetApplicationUseCase } from '@/application/smallBusiness/application/GetApplicationUseCase';
import { CalendlyEvent } from '@/consent/medicare/domain/CalendlyEventResponse';
import { SmallBusinessPrimaryContact } from '@/application/smallBusiness/domain/SmallBusinessPrimaryContact';
import { SendLeadInfoUseCase } from '@/application/smallBusiness/application/SendLeadInfoUseCase';
import { SmallBusinessLeadInfo } from '@/application/smallBusiness/domain/SmallBusinessLeadInfo';
import { AppStore } from '@/shared/infrastructure/store/AppStore';
import { SetApplicationSentUuidUseCase } from '@/application/smallBusiness/application/SetApplicationSentUuidUseCase';
import { SmallBusinessSentAppsCookie } from '@/application/smallBusiness/infrastructure/dataSource/SmallBusinessSentAppsCookie';
import { IsApplicationUuidSendableUseCase } from '@/application/smallBusiness/application/IsApplicationUuidSendableUseCase';
import { SmallBusinessMember } from '@/application/smallBusiness/domain/SmallBusinessMember';
import { UpdateApplicationUseCase } from '@/application/smallBusiness/application/UpdateApplicationUseCase';
import { SMALL_COMPANY_SIZE } from '@/application/smallBusiness/infrastructure/constants';

const enrollmentRepository = new EnrollmentApi();
const smallBusinessRepository = new SmallBusinessApi();
const smallBusinessSentAppsRepository = new SmallBusinessSentAppsCookie();

const addressValidation = new AddressValidationUseCase(enrollmentRepository);
const saveAndExit = new SaveAndExitUseCase(enrollmentRepository);
const fetchAvailablePlans = new FetchAvailablePlansUseCase(smallBusinessRepository);
const submitApplication = new SubmitApplicationUseCase(smallBusinessRepository);
const updateApplication = new UpdateApplicationUseCase(smallBusinessRepository);
const getApplication = new GetApplicationUseCase(smallBusinessRepository);
const sendLeadInfo = new SendLeadInfoUseCase(smallBusinessRepository);
const setSentApplicationUuid = new SetApplicationSentUuidUseCase(smallBusinessSentAppsRepository);
const isApplicationUuidSendable = new IsApplicationUuidSendableUseCase(smallBusinessSentAppsRepository);

interface LeadInfoCacheItem {
  appId: UUID;
  data: string;
}

export interface SmallBusinessStoreInterface {
  application: SmallBusinessApplication,
  companySize: number,
  isSmallCompany: boolean,
  loading: boolean,
  forceSelectedAddress: boolean,
  availablePlans: Array<SmallBusinessPlan>,
  filteredPlans: Array<SmallBusinessPlan>,
  activeFiltersCount: number,
  availablePlansLoading: boolean,
  disableAddPlans: boolean,
  loadingApplication: boolean
  availableFilters: Array<PlanFilter>,
  updateAvailableFilters: boolean,
  dependentToFocus: UUID,
  isReadOnly: boolean,
  hasAppointment: boolean,
}

const { getDateFromAge } = dateHelpers();
const { formatPrice } = formatHelpers();

const defaultSampleEmployee = {
  id: uuid(),
  gender: GENDERS.MALE,
  county: '',
  date_of_birth: getDateFromAge(30),
  relationship: Relationships.Self.key as Relationship,
  dependents: [],
} as SmallBusinessEmployee;

const initialState: SmallBusinessStoreInterface = {
  application: {
    uuid: uuid(),
    company: {
      primary_contact: {} as SmallBusinessPrimaryContact,
      main_address: {} as Address,
      is_mailing_address_same_as_main: true,
      sms_phone_consent: false,
      name: '',
    },
    applicants: [],
    appointment: {} as CalendlyEvent,
    plans: [] as Array<SmallBusinessPlan>,
    contribution: {
      employee_amount: 50,
      dependent_amount: 0,
      type: ContributionType.Percentage
    } as SmallBusinessContribution,
    filters: {
      cal_choice: false,
    },
    effective_date: 0,
    sample_employee: defaultSampleEmployee,
    detailed_employees: false
  },
  companySize: 0,
  isSmallCompany: false,
  availablePlans: [] as Array<SmallBusinessPlan>,
  filteredPlans: [] as Array<SmallBusinessPlan>,
  activeFiltersCount: 0,
  availablePlansLoading: false,
  disableAddPlans: false,
  loading: false,
  forceSelectedAddress: false,
  loadingApplication: false,
  availableFilters: [] as Array<PlanFilter>,
  updateAvailableFilters: false,
  dependentToFocus: '',
  isReadOnly: false,
  hasAppointment: false,
};

export class SmallBusinessStore extends Ploc<SmallBusinessStoreInterface> {

  private static readonly LEAD_INFO_STORAGE_KEY = 'smallBusinessLeadInfo';

  public constructor () {
    super(initialState);
  }

  public async initAppFromDatabase (application: SmallBusinessApplication): Promise<void> {
    application.plans = this.setExtraPlanAttributes(application.plans);
    await this.changeState({ ...this.state, application, isReadOnly: !!application.referenceGroupId });
    await this.updateApplication(application);
    await this.handleApplicantsLengthChange();
    await this.checkPlansLimit();
    await this.hasAppointmentCheck();
  }

  public async updateApplication (value:Partial<SmallBusinessApplication>): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, ...value } });
    this.saveApplicationOnLocalStorage();
  }

  public async updateCompany (value:Partial<SmallBusinessCompany>): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, company: { ...this.state.application.company, ...value } } });
    this.editSampleApplicant({
      ...this.state.application.sample_employee,
      zip_code: this.state.application.company.main_address.zip_code,
      county: this.state.application.company.main_address.county,
    });
  }

  public async updateEmployee (value:Partial<SmallBusinessEmployee>): Promise<void> {
    const applicants = this.state.application.applicants.map(applicant => applicant.id === value.id ? { ...applicant, ...value } : applicant);

    this.updateApplication({ applicants });
  }

  public async getApplication (uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loadingApplication: true });
    const response = await getApplication.run(uuid);

    if (response.success && response.data) {
      const application = response.data;
      application.uuid = uuid;
      this.initAppFromDatabase(application);
      this.saveApplicationOnLocalStorage();
    }
    this.changeState({ ...this.state, loadingApplication: false });

    return response;
  }

  public async addEmployee (amount?: number, applicant?: SmallBusinessEmployee): Promise<void> {
    const applicants = this.state.application.applicants;
    const numberOfEmployees = amount && !isNaN(amount) ? amount : 1;
    for (let i = 0; i < numberOfEmployees; i++) {
      applicants.push(applicant ?? {
        id: uuid(),
        first_name: `Employee${applicants.length + 1}`,
        last_name: `Employee${applicants.length + 1}`,
        dependents: [],
        gender: GENDERS.MALE,
        date_of_birth: getDateFromAge(30),
        zip_code: this.state.application.company.main_address.zip_code,
        county: this.state.application.company.main_address.county,
        relationship: Relationships.Self.key as Relationship
      });
    }
    this.updateApplication({ applicants: [...applicants] });
    this.handleApplicantsLengthChange();
  }

  public async removeEmployee (uuid: UUID): Promise<void> {
    const applicants = this.state.application.applicants;
    const index = applicants.findIndex(applicant => applicant.id === uuid);
    const newApplicants = applicants.toSpliced(index, 1);

    this.updateApplication({ applicants: newApplicants });
    this.handleApplicantsLengthChange();
  }

  public async removeAllEmployees (): Promise<void> {
    this.updateApplication({ applicants: [] });
    this.handleApplicantsLengthChange();
  }

  public async addDependent (applicant: SmallBusinessEmployee, relationship: Relationship): Promise<void> {
    const newDependent = {
      id: uuid(),
      first_name: this.getDependentDefaultName(applicant, relationship),
      last_name: applicant.last_name,
      gender: GENDERS.MALE,
      date_of_birth: 0,
      county: applicant.county,
      zip_code: applicant.zip_code,
      relationship: relationship,
    };
    this.changeState({ ...this.state, dependentToFocus: newDependent.id });
    this.updateEmployee({ dependents: [...applicant.dependents, newDependent], id: applicant.id });
  }

  public async updateDependent (applicant: SmallBusinessEmployee, dependent: SmallBusinessMember): Promise<void> {
    const dependents = [...applicant.dependents];
    const index = dependents.findIndex(d => d.id === dependent.id);
    dependents[index] = dependent;
    this.updateEmployee({ dependents: dependents, id: applicant.id });
  }

  public async removeDependent (applicant: SmallBusinessEmployee, dependentId: UUID): Promise<void> {
    const newDependents = applicant.dependents.filter((dependent) => dependent.id !== dependentId);
    this.updateEmployee({ dependents: newDependents, id: applicant.id });
  }

  public async editSampleApplicant (sampleApplicant: SmallBusinessEmployee): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, sample_employee: sampleApplicant } });
  }

  public async validateAddress (address: Address): Promise<ServicesApiResponse<AddressValidationResponse>> {
    return await addressValidation.run(address);
  }

  public async submitApplication (): Promise<SfApiResponse> {
    const response = await submitApplication.run(this.state.application);
    if (response.data && response.success) {
      await this.updateApplication({ referenceGroupId: response.data.referenceGroupId });
      await setSentApplicationUuid.run(this.state.application.uuid);
    }

    return response;
  }

  public async saveApplication (): Promise<SfApiResponse> {
    const response = await updateApplication.run(this.state.application);

    return response;
  }

  public async submitForm (): Promise<SfApiResponse> {
    if (this.state.application.referenceGroupId) {
      return await this.saveApplication();
    }

    return await this.submitApplication();
  }

  public async isSendableApplicationUuid (uuid: UUID): Promise<SfApiResponse> {
    if (this.state.application.referenceGroupId) {
      return {
        success: true,
      };
    }

    return await isApplicationUuidSendable.run(uuid);
  }

  public async saveApplicationOnLocalStorage (): Promise<SfApiResponse> {
    const responseCheck = await this.isSendableApplicationUuid(this.state.application.uuid);
    if (responseCheck && responseCheck.success) {
      localStorage.setItem('smallBusinessApplication', JSON.stringify(this.state.application));
    }

    return responseCheck;
  }

  public async initAppFromLocalStorage (): Promise<void> {
    if (!localStorage.getItem('smallBusinessApplication')) {
      return;
    }
    await this.changeState({ ...this.state, loadingApplication: true });
    const application = JSON.parse(localStorage.getItem('smallBusinessApplication') || '{}') as SmallBusinessApplication;

    if (!application.sample_employee) {
      application.sample_employee = defaultSampleEmployee;
    }

    if (application.detailed_employees === undefined) {
      application.detailed_employees = true;
    }

    if ('plan' in application && !application.plans) {
      application.plans = [(application as unknown as { plan: SmallBusinessPlan }).plan];
      // @ts-expect-error Property 'plan' does not exist on type
      delete (application as unknown as { plan: SmallBusinessPlan }).plan;
    }

    const responseCheck = await this.isSendableApplicationUuid(application.uuid);

    await this.changeState({ ...this.state, isReadOnly: !!application.referenceGroupId, loadingApplication: false, hasAppointment: this.isSavedAndCompletedSmallCompany(application) || !!application.appointment?.appointment_id });
    if (!application.referenceGroupId && (responseCheck && responseCheck.error)) {
      this.clearAndRestart();

      return;
    }

    await this.changeState({ ...this.state, application: application });
    await this.handleApplicantsLengthChange();
    await this.checkPlansLimit();
    await this.hasAppointmentCheck();
  }

  public async saveAndExit (email: string, application: unknown, uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await saveAndExit.run([APPLICATION_TYPES.SMALL_BUSINESS, email, application, uuid]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async setForceSelectedAddress (value: boolean): Promise<void> {
    this.changeState({ ...this.state, forceSelectedAddress: value });
  }

  public async fetchAvailablePlans (): Promise<void> {
    const appStore = getStore<AppStore>('AppStore', AppStore);
    this.changeState({ ...this.state, availablePlansLoading: true });

    const response = await fetchAvailablePlans.run({
      company: this.state.application.company,
      contribution: this.state.application.contribution,
      applicants: this.state.application.detailed_employees ? this.state.application.applicants : [this.state.application.sample_employee],
      effective_date: this.state.application.effective_date,
      filters: {
        cal_choice: !!this.state.application.filters?.cal_choice,
      },
    });
    this.changeState({ ...this.state, availablePlans: response.data?.plans ?? [], availablePlansLoading: false });
    if (response.data?.filters) {
      this.generateAvailableFiltersFromApi(response.data?.filters);
    }

    if (response.error) {
      await appStore.addToast({ type: ToastTypes.DANGER, message: response.error.response?.data.error || 'Error getting the plans. Please try again.' });
    }
  }

  public generateAvailableFiltersFromApi (apiFilters: SmallBusinessApiFilters): void {
    const newAvailableFilters: Array<PlanFilter> = [];
    const tierOrder = ['bronze', 'silver', 'gold', 'platinum'];

    Object.keys(apiFilters).forEach((key: string) => {
      const apiFilter = apiFilters[key as keyof SmallBusinessApiFilters];
      const newFilter: PlanFilter = {
        name: this.getFilterNameFromKey(key),
        key: key,
        options: []
      };
      const oldFilter = this.state.availableFilters.find((filter) => filter.name === newFilter.name);
      apiFilter?.forEach((option) => {
        const oldOption = oldFilter?.options.find((o) => o.key === option.key);
        newFilter.options.push({
          key: option.key,
          text: option.value,
          value: oldOption?.value || false,
        });
      });
      if (key === 'tier') {
        newFilter.options.sort((a, b) => tierOrder.indexOf(a.text.toLowerCase()) - tierOrder.indexOf(b.text.toLowerCase()));
      } else {
        newFilter.options.sort((a, b) => a.text.localeCompare(b.text));
      }
      newAvailableFilters.push(newFilter);
    });

    newAvailableFilters.sort((a, b) => a.name.localeCompare(b.name));

    this.setFilters(newAvailableFilters);
  }

  public applyPlanFilters (): void {
    const plans = this.state.availablePlans.filter((plan) => {
      return this.state.availableFilters.every((filter) => {
        const selectedOptions = filter.options.filter(option => option.value);
        if (selectedOptions.length === 0) {
          return true;
        }

        return selectedOptions.some(option => {
          if (filter.key === 'sub_carrier') {
            return plan.sub_carrier?.code_name === option.key;
          }
          if (filter.key === 'type') {
            return plan.type === option.key.toUpperCase();
          }
          if (filter.key === 'tier') {
            return plan.metal_tier === option.key;
          }

          return plan[filter.key as keyof SmallBusinessPlan] === option.key;
        });
      });
    });
    this.changeState({ ...this.state, filteredPlans: plans });
    this.setFilterOptionsAmount();
    this.setFilteredPlansExtraAttributes();
  }

  public countPlanFilters (): void {
    const filtersCount = this.state.availableFilters.reduce((count, filter) => {
      return count + filter.options.filter(option => option.value).length;
    }, 0);

    return this.changeState({ ...this.state, activeFiltersCount: filtersCount });
  }

  public setFilterOptionsAmount (): void {
    const newAvailableFilters = this.state.availableFilters.map((filter) => {
      const newOptions = filter.options.map((option) => {
        const amount = this.getPlanOptionAmount(filter.key, option.key);

        return {
          ...option,
          amount: amount,
        };
      });

      return {
        ...filter,
        options: newOptions,
      };
    });
    this.changeState({ ...this.state, availableFilters: newAvailableFilters });
  }

  public setFilteredPlansExtraAttributes (): void {
    const plans = this.setExtraPlanAttributes(this.state.filteredPlans);

    this.changeState({ ...this.state, filteredPlans: plans });
  }

  private setExtraPlanAttributes (plans: Array<SmallBusinessPlan>): Array<SmallBusinessPlan> {
    return plans.map((plan) => {
      const totalEmployerContribution = plan.price_breakdown.reduce((acc, price) => acc + price.employer_cost, 0);
      const totalEmployeeCost = plan.price_breakdown.reduce((acc, price) => acc + price.employee_cost, 0);
      const twoLineName = plan.display_name.join('<br>');
      const totalPlanRate = plan.price_breakdown.reduce((acc, price) => acc + price.rate, 0);
      const details = plan.details;
      const newDetails = [
        {
          id: 'PricingDetails',
          title: 'Pricing Details',
          description: '',
        },
        {
          id: 'TotalEmployerContribution',
          title: 'Total Employer Contribution',
          description: `${formatPrice(totalEmployerContribution)}`,
        },
        {
          id: 'EstimatedTotalCost',
          title: 'Estimated Total Cost',
          description: `${formatPrice(totalPlanRate)}`,
        }
      ];
      details.unshift(...newDetails);

      return { ...plan, totalEmployerContribution, totalEmployeeCost, twoLineName, details };
    });
  }

  public getPlanOptionAmount (filterKey: string, optionKey: string): number {
    const plans = this.state.filteredPlans.filter(plan => {
      if (filterKey === 'sub_carrier') {
        return plan.sub_carrier?.code_name === optionKey;
      }
      if (filterKey === 'type') {
        return plan.type === optionKey.toUpperCase();
      }
      if (filterKey === 'tier') {
        return plan.metal_tier === optionKey;
      }
    });

    return plans.length;
  }

  public getFilterNameFromKey (key: string): string {
    switch (key) {
      case 'sub_carrier':
        return 'Carrier';
      case 'type':
        return 'Plan Type';
      case 'tier':
        return 'Metal Tier';
      default:
        return key;
    }
  }

  public async addSelectedPlan (plan: SmallBusinessPlan): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, plans: [...this.state.application.plans, plan] } });
    this.checkPlansLimit();
  }

  public async removeAllSelectedPlans (): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, plans: [] } });
    this.checkPlansLimit();
  }

  public async removeSelectedPlan (plan: SmallBusinessPlan): Promise<void> {
    const plans = this.state.application.plans.filter((p) => p.reference_id !== plan.reference_id);
    this.changeState({ ...this.state, application: { ...this.state.application, plans } });
    this.checkPlansLimit();
    this.saveApplicationOnLocalStorage();
  }

  public async checkPlansLimit () : Promise<void> {
    if (this.state.application.plans.length >= 1) {
      return this.changeState({ ...this.state, disableAddPlans: true });
    }
    this.changeState({ ...this.state, disableAddPlans: false });
  }

  public getAvailablePlans (): Array<SmallBusinessPlan> {
    return this.state.filteredPlans;
  }

  public async setCompanySize (companySize: number): Promise<void> {
    this.changeState({ ...this.state, companySize });
    this.handleApplicantsLengthChange();
  }

  public async setEffectiveDate (effectiveDate: number): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, effective_date: effectiveDate } });
  }

  public async setContribution (contribution: Partial<SmallBusinessContribution>): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, contribution: { ...this.state.application.contribution, ...contribution } } });
  }

  public async setFilters (filters: Array<PlanFilter>): Promise<void> {
    this.changeState({ ...this.state, availableFilters: filters });
    this.applyPlanFilters();
  }

  public async setCalChoiceFilter (value: boolean): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, filters: { ...this.state.application.filters, cal_choice: value } } });
  }

  public async enableUpdateAvailableFilters (): Promise<void> {
    this.changeState({ ...this.state, updateAvailableFilters: true });
  }

  public async updateDetailedEmployeesFlag (value: boolean): Promise<void> {
    this.changeState({ ...this.state, application: { ...this.state.application, detailed_employees: value, plans: [] } });
  }

  public findEmployeeDataCoverage (uuid: UUID): string {
    const employee = this.state.application.applicants.find((employee) => employee.id === uuid);

    return employee?.dependents.length ? `Employee + ${employee.dependents.length}` : 'Employee Only';
  }

  public handleApplicantsLengthChange (): void {
    this.changeState({ ...this.state, application: { ...this.state.application, filters: { ...this.state.application.filters, cal_choice: this.state.companySize >= 6 ? true : false } } });
    if (!this.state.application.detailed_employees) {
      this.changeState({ ...this.state, isSmallCompany: this.state.companySize <= SMALL_COMPANY_SIZE });

      return;
    }
    this.changeState({ ...this.state, isSmallCompany: this.isSmallCompany(this.state.application) });
    this.changeState({ ...this.state, companySize: this.state.application.applicants.length });
  }

  public hasAppointmentCheck (): void {
    this.changeState({ ...this.state, hasAppointment: this.isSavedAndCompletedSmallCompany(this.state.application) || !!this.state.application.appointment?.appointment_id });
  }

  public clearReviewData (): void {
    localStorage.removeItem('smallBusinessApplicationReview');
  }

  public clearAndRestart (): void {
    this.resetState();
    localStorage.removeItem('smallBusinessApplication');
    this.changeState({ ...this.state, isSmallCompany: true, companySize: 0, application: { ...initialState.application, uuid: uuid() } });
  }

  private isSavedAndCompletedSmallCompany (application: SmallBusinessApplication): boolean {
    return this.isSmallCompany(application) && !!application.referenceGroupId;
  }

  private isSmallCompany (application: SmallBusinessApplication): boolean {
    return application.applicants.length <= SMALL_COMPANY_SIZE;
  }

  private getLeadInfoCache (): Array<LeadInfoCacheItem> {
    try {
      const cached = localStorage.getItem(SmallBusinessStore.LEAD_INFO_STORAGE_KEY);

      return cached ? JSON.parse(cached) : [];
    } catch (_error) {
      return [];
    }
  }

  private updateLeadInfoCache (cacheData: Array<LeadInfoCacheItem>): void {
    localStorage.setItem(SmallBusinessStore.LEAD_INFO_STORAGE_KEY, JSON.stringify(cacheData));
  }

  private extractCompanyLeadInfo (): SmallBusinessLeadInfo {
    const { company } = this.state.application;

    return {
      group_size: this.state.companySize,
      phone: company.primary_contact.phone,
      email: company.primary_contact.email,
      zip_code: company.main_address.zip_code,
    };
  }

  public async sendLeadInfo (): Promise<SfApiResponse<void> | null> {
    const appId = this.state.application.uuid;
    const companyInfo = this.extractCompanyLeadInfo();
    const companyInfoJson = JSON.stringify(companyInfo);

    const existingCache = this.getLeadInfoCache();
    const existingEntry = existingCache.find(item => item.appId === appId);

    if (existingEntry) {
      const hasChanged = existingEntry.data !== companyInfoJson;
      existingEntry.data = companyInfoJson;
      this.updateLeadInfoCache(existingCache);

      if (!hasChanged) {
        return null;
      }
    } else {
      const newCache = [...existingCache, { appId, data: companyInfoJson }];
      this.updateLeadInfoCache(newCache);
    }

    return await sendLeadInfo.run(companyInfo);
  }

  public async clearDependentToFocus (): Promise<void> {
    this.changeState({ ...this.state, dependentToFocus: '' });
  }

  // ---------------- Private Methods ----------------

  private getDependentDefaultName (applicant: SmallBusinessEmployee, relationship: Relationship): string {
    if (relationship === Relationships.Spouse.key) {
      return 'Spouse';
    }
    const applicantChildren = applicant.dependents.filter(dependent => dependent.relationship === Relationships.Child.key);

    return `Child${applicantChildren.length + 1}`;
  }

}
